import type { ChatMessage, ChatStatus, Thread } from 'tailwind-v4/blocks/chat'
import type {
  SupportCaseSummary,
  SupportHistoryMessage,
} from '@/state/trunk/trunk.types'

/**
 * Pure mapping between trunk's support wire shapes and the `chat` block's
 * `Thread` / `ChatMessage`. No React and no socket in here, so it is unit
 * tested on its own and `useSupportChat` stays a thin state machine.
 *
 * The transcript has four voices. The customer looking at the screen is the
 * `user`. The AI is the `assistant`. A named human on the other side (a
 * Stampede teammate, or a colleague of the customer in a shared case) is also
 * `assistant`-side but carries `authorKind: 'human'` so the block renders
 * their text raw under their own name. Joins, takeovers and hand-backs are
 * `system` notes.
 */

/** The customer-visible persona (decision 8 in the context doc). */
export const ASSISTANT_NAME = 'Stampede Assistant'

/** Customer-facing wording for a case status. Unknown statuses pass through. */
const STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  'agent-handling': 'In progress',
  'human-handling': 'With the team',
  'waiting-customer': 'Awaiting your reply',
  resolved: 'Resolved',
}

export function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status
}

/** The case's subject, or a generic title when none was ever set. */
export function caseTitle(subject: string | null | undefined): string {
  const trimmed = subject?.trim()
  return trimmed ? trimmed : 'Support conversation'
}

/** A support case as a sidebar thread. The sidebar groups by `updatedAt`. */
export function caseToThread(c: SupportCaseSummary): Thread {
  const bits = [statusLabel(c.status)]
  if (c.needsHuman && c.status !== 'resolved') bits.push('Team notified')
  return {
    id: c.caseId,
    title: caseTitle(c.subject),
    preview: bits.join(' · '),
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
    metadata: { status: c.status, needsHuman: c.needsHuman },
  }
}

/** Most recent activity first. Does not mutate the input. */
export function sortByRecency(
  cases: SupportCaseSummary[]
): SupportCaseSummary[] {
  // ISO timestamps: lexical order is chronological order.
  return [...cases].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
}

/**
 * One human or agent turn as trunk sends it, either as a history row or as a
 * live `message` echo. The two differ only in which optional fields are set.
 */
export interface SupportTurn {
  id: string
  /** Mastra role: `assistant` for the agent, `signal` or `user` for people. */
  role?: string
  /** `customer`, `staff` or `agent`. Trunk treats a missing value as customer. */
  functionalRole?: string
  authorId?: string
  authorName?: string
  text: string
  createdAt: string
}

export function historyToTurn(m: SupportHistoryMessage): SupportTurn {
  return {
    id: m.id,
    role: m.role,
    functionalRole: m.functionalRole,
    authorId: m.authorId,
    authorName: m.authorName,
    text: m.text,
    createdAt: m.createdAt,
  }
}

export function isAgentTurn(t: SupportTurn): boolean {
  return t.functionalRole === 'agent' || t.role === 'assistant'
}

/**
 * Whether a turn was typed by the person looking at the screen. Rows written
 * before trunk recorded author ids have no `authorId`; those cases only ever
 * had one customer, so a customer turn without an id is treated as theirs.
 */
export function isOwnTurn(t: SupportTurn, myUid: string | undefined): boolean {
  if (isAgentTurn(t)) return false
  if ((t.functionalRole ?? 'customer') !== 'customer') return false
  if (!t.authorId || !myUid) return true
  return t.authorId === myUid
}

function textParts(text: string): ChatMessage['parts'] {
  return [{ type: 'text', text }]
}

/** A finished turn as a transcript message. */
export function turnToChatMessage(
  t: SupportTurn,
  myUid: string | undefined
): ChatMessage {
  const base = {
    id: t.id,
    parts: textParts(t.text),
    createdAt: t.createdAt,
    status: 'complete' as const,
  }
  if (isAgentTurn(t)) return { ...base, role: 'assistant' }
  if (isOwnTurn(t, myUid)) return { ...base, role: 'user' }
  const name = t.authorName?.trim()
  let authorName: string
  if (t.functionalRole === 'staff') {
    authorName = name ? `${name} from Stampede` : 'Stampede Support'
  } else {
    authorName = name ? name : 'Your colleague'
  }
  return { ...base, role: 'assistant', authorKind: 'human', authorName }
}

/** A centred note in the transcript: someone joined, took over, handed back. */
export function systemMessage(
  id: string,
  text: string,
  createdAt: string
): ChatMessage {
  return { id, role: 'system', parts: textParts(text), createdAt }
}

/** An agent reply that is still arriving. */
export function streamingAgentMessage(
  runId: string,
  text: string,
  createdAt: string
): ChatMessage {
  return {
    id: runId,
    role: 'assistant',
    parts: textParts(text),
    createdAt,
    status: 'streaming',
  }
}

type Part = ChatMessage['parts'][number]

function textOf(m: ChatMessage): string {
  return m.parts
    .filter(
      (p: Part): p is Extract<Part, { type: 'text' }> => p.type === 'text'
    )
    .map((p: Extract<Part, { type: 'text' }>) => p.text)
    .join('')
}

/** Append a token to the run's message, creating the message if it is the first token. */
export function appendToken(
  messages: ChatMessage[],
  runId: string,
  delta: string,
  createdAt: string
): ChatMessage[] {
  const idx = messages.findIndex((m) => m.id === runId)
  if (idx === -1) {
    return [...messages, streamingAgentMessage(runId, delta, createdAt)]
  }
  const current = messages[idx]
  const next = [...messages]
  next[idx] = {
    ...current,
    parts: textParts(textOf(current) + delta),
    status: 'streaming',
  }
  return next
}

/** Mark one run (or every in-flight message when `runId` is absent) as finished. */
export function completeRun(
  messages: ChatMessage[],
  runId?: string
): ChatMessage[] {
  return messages.map((m) =>
    m.status === 'streaming' && (!runId || m.id === runId)
      ? { ...m, status: 'complete' }
      : m
  )
}

/**
 * What the composer and the thinking indicator should show. `thinking` is
 * trunk's `agent_thinking` signal, which arrives before the first token while
 * the agent is running tools. A message mid-stream also counts.
 */
export function deriveStatus(
  thinking: boolean,
  messages: ChatMessage[]
): ChatStatus {
  if (thinking) return 'streaming'
  return messages.some((m) => m.status === 'streaming') ? 'streaming' : 'idle'
}
